import { useState, useEffect, forwardRef, useMemo } from 'react';
import { Combobox } from '@headlessui/react';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { AutocompleteSearch } from 'types/Filters';
import { NIcon } from '@innersource-nn/novoui-react';

interface AutocompleteSearchBarProps {
    onTermChange: (term: string) => void;
    items: AutocompleteSearch[];
    loading: boolean;
    placeholder: string;
    setFiltersOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function AutocompleteSearchBar({
    onTermChange,
    items,
    loading,
    placeholder,
    setFiltersOpen,
}: AutocompleteSearchBarProps) {
    const navigate = useNavigate();
    const location = useLocation();

    const [searchTerm, setSearchTerm] = useState('');
    const [arrowDownPressed, setArrowDownPressed] = useState(false);
    const [activeOptionIndex, setActiveOptionIndex] = useState<number | null>(
        null,
    );

    useEffect(() => {
        if (!location.search) {
            onTermChange('');
            setSearchTerm('');
        } else {
            const searchParams = createSearchParams(location.search).get(
                'query',
            );
            setSearchTerm(searchParams || '');
            onTermChange(searchParams || '');
        }
    }, [location]);

    const filteredItems = useMemo(() => {
        if (searchTerm === '') {
            return items;
        } else {
            return items?.filter((item) => {
                return item.label
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());
            });
        }
    }, [searchTerm, items]);

    const handleClick = (item: AutocompleteSearch) => {
        if (item) navigate(`/${item.type}/${item.uri}`);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (activeOptionIndex !== null) {
                handleClick(filteredItems[activeOptionIndex]);
            } else {
                if (setFiltersOpen) {
                    setFiltersOpen(true);
                }
                navigate({
                    pathname: location.pathname,
                    search: `query=${searchTerm}`,
                });
            }
        } else if (e.key === 'ArrowDown') {
            setArrowDownPressed(true);
            setActiveOptionIndex((prevIndex) =>
                prevIndex === null
                    ? 0
                    : Math.min(prevIndex + 1, filteredItems.length - 1),
            );
        } else if (e.key === 'ArrowUp') {
            if (activeOptionIndex === 0) {
                setArrowDownPressed(false);
                setActiveOptionIndex(null);
            } else {
                setActiveOptionIndex((prevIndex) =>
                    prevIndex === null ? null : Math.max(prevIndex - 1, 0),
                );
            }
        }
    };

    let MyCustomButton = forwardRef(function () {
        return (
            <button
                onClick={() => setSearchTerm('')}
                className="bg-white mt-8 mb-4 px-8 rounded-r-8 border-y-1 border-r-1 border-y-faded-blue-400 border-r-faded-blue-400"
            >
                <NIcon iconName="close" size={16} className="fill-true-blue" />
            </button>
        );
    });

    return (
        <div className="relative mb-32">
            <Combobox>
                <div className="flex">
                    <Combobox.Input
                        onChange={(e) => setSearchTerm(e.target.value)}
                        displayValue={(item: AutocompleteSearch) => item.label}
                        value={searchTerm}
                        placeholder={placeholder}
                        className="autocomplete-search-bar-input text-true-blue rounded-l-8"
                        onKeyDown={handleKeyDown}
                    />
                    <Combobox.Button as={MyCustomButton} />
                </div>
                <Combobox.Options className="search-bar-div rounded">
                    {!loading &&
                        filteredItems.map((item, index) => (
                            <Combobox.Option
                                key={item.uri}
                                value={item}
                                onClick={() => handleClick(item)}
                                onMouseEnter={() => setActiveOptionIndex(index)}
                            >
                                <div
                                    className={clsx(
                                        activeOptionIndex === index &&
                                            arrowDownPressed &&
                                            'bg-sea-blue-100',
                                        'dropdown-options hover:bg-sea-blue-100',
                                    )}
                                >
                                    {item.label}
                                </div>
                            </Combobox.Option>
                        ))}
                </Combobox.Options>
            </Combobox>
        </div>
    );
}
